import type {SplitText} from 'gsap/SplitText'

interface TitlePart {
  text: string
  group: number
  isImpact: boolean
}

export interface IntroTitle {
  lines: Array<{text: string; group: number}>
  parts: TitlePart[] | null
}

export const parseIntroTitle = (title: string): IntroTitle => {
  // Without animation markers, leave the heading unchanged.
  if (!/[|[\]]/.test(title)) return {lines: [{text: title, group: 0}], parts: []}

  // Read the || separated lines, then the | separated animation groups within each line.
  const authoredLines = title.split('||')
  const lines: IntroTitle['lines'] = []
  const parts: TitlePart[] = []
  let groupIndex = 0

  for (const line of authoredLines) {
    const firstGroup = groupIndex
    const lineParts: TitlePart[] = []

    for (const group of line.split('|')) {
      // Each group can contain one [impact] range. Capture the text before it,
      // inside it and after it separately; without brackets, all the text is plain.
      const match = /^([^[\]]*)(?:\[([^[\]]+)\]([^[\]]*))?$/.exec(group)
      if (!match || !group.replaceAll(/[[\]]/g, '').trim()) {
        // A group must have text and at most one complete [bracketed range].
        // For example, "[Software" is missing its closing bracket, and "Software|"
        // leaves an empty group after |. Keep the heading text, but skip its animation.
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console -- Authoring feedback only; do not report cosmetic failures in production.
          console.warn('Invalid homepage title animation markers; skipping animation.')
        }

        return {
          lines: authoredLines.map((text, index) => ({text: text.replaceAll(/[|[\]]/g, ''), group: index})),
          parts: null,
        }
      }

      // Keep the nonempty captures in the same group, marking only the middle
      // capture for impact. Retain spaces exactly as authored.
      const index = groupIndex++
      lineParts.push(
        ...match.slice(1).flatMap((text, partIndex) => (text ? [{text, group: index, isImpact: partIndex === 1}] : [])),
      )
    }

    // Joining those parts gives the line to display, with the markers removed.
    parts.push(...lineParts)
    lines.push({text: lineParts.map(part => part.text).join(''), group: firstGroup})
  }

  return {lines, parts}
}

export const createTitleFragments = (split: SplitText, parts: TitlePart[], lines: IntroTitle['lines']): boolean => {
  let partIndex = 0
  let partOffset = 0
  let lineIndex = 0
  const fullText = parts.map(part => part.text).join('')

  // SplitText duplicates leading spaces with character delimiters, so restore
  // edge whitespace after measuring each trimmed, authored line.
  for (const {text} of lines) {
    const firstLine = split.lines[lineIndex]
    let remaining = text.trim().length

    // An authored line can wrap onto several measured lines. Find its last one
    // before putting back the spaces from either end of the authored text.
    while (remaining > 0) {
      const line = split.lines[lineIndex++]
      if (!line) return false
      remaining -= line.textContent?.length ?? 0
    }

    if (remaining !== 0) return false

    firstLine?.prepend(document.createTextNode(text.slice(0, text.length - text.trimStart().length)))
    split.lines[lineIndex - 1]?.append(document.createTextNode(text.slice(text.trimEnd().length)))
  }

  if (split.lines.map(line => line.textContent ?? '').join('') !== fullText) return false

  // The heading can wrap in the middle of an authored group. Putting that whole
  // group in one inline-block for animation could change where it wraps.
  // Process the lines SplitText already measured separately, so each animation
  // span stays within an existing line.
  for (const line of split.lines) {
    let remaining = line.textContent?.length ?? 0
    const nodes: Node[] = []

    // While there is text left on this line, take the portion of the current
    // authored part that belongs on it.
    while (remaining > 0) {
      const part = parts[partIndex]
      if (!part) return false

      const length = Math.min(part.text.length - partOffset, remaining)
      const text = part.text.slice(partOffset, partOffset + length)
      const content = text.trim()

      // If that portion contains non-whitespace text, wrap it in a span for animation,
      // leaving any surrounding spaces outside the span.
      if (content) {
        const start = text.indexOf(content)
        const fragment = document.createElement('span')
        fragment.className = 'word'
        fragment.setAttribute('data-reveal-group', String(part.group))

        // If the authored part has an impact marker, mark this span for the colour transition.
        if (part.isImpact) fragment.setAttribute('data-impact', '')

        fragment.style.display = 'inline-block'
        fragment.textContent = content
        nodes.push(
          document.createTextNode(text.slice(0, start)),
          fragment,
          document.createTextNode(text.slice(start + content.length)),
        )
      } else {
        // Otherwise, it is only whitespace, so add it without an animation span.
        nodes.push(document.createTextNode(text))
      }

      // Then subtract the characters we just copied from this line's remaining count
      // and advance within the authored part, moving to the next part if this one is finished.
      remaining -= length
      partOffset += length
      if (partOffset === part.text.length) {
        partIndex++
        partOffset = 0
      }
    }

    // Once the whole line is accounted for, replace SplitText's per-character spans
    // with the animation spans and spaces we just collected.
    line.replaceChildren(...nodes)
  }

  return partIndex === parts.length
}
